import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import GetBestWorstTopPlans from "../../api/performance/GetBestWorstTopPlans";
import { Link } from "react-router-dom";
import { Spin, Avatar } from "antd";
import { ExclamationCircleOutlined, UserOutlined } from "@ant-design/icons";
import Theme from "../../theme/Theme";

function AttentionRequired() {
  const [worstTopPlans, setWorstTopPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    getWorstPlans();
  }, [perfCycleState]);

  async function getWorstPlans() {
    setLoading(true);
    const apiResponse = await GetBestWorstTopPlans(
      positionState?.id,
      perfCycleState?.id
    );
    if (apiResponse.success) {
      setWorstTopPlans(apiResponse?.data?.worstTopPlans?.slice(0, 3) || []);
    }
    setLoading(false);
  }

  function getReasonText(plan, index) {
    const perf = parseFloat(plan?.performance || 0);
    if (perf < 30) return `KPI dropped to ${perf.toFixed(0)}% — needs evaluation`;
    if (perf < 50) return `Critical: Stale for ${plan?.daysSinceUpdate || 0} days & Overdue`;
    return `Performance at ${perf.toFixed(0)}% — below threshold`;
  }

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex justify-center items-center" style={{ minHeight: "120px" }}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!worstTopPlans || worstTopPlans.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div
          className="flex items-center gap-2"
          style={{
            fontSize: "18px",
            fontWeight: 600,
            color: Theme().textPrimary,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <ExclamationCircleOutlined style={{ color: Theme().primary }} />
          Attention Required
        </div>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: Theme().textPrimary,
            fontFamily: "Inter, monospace",
            letterSpacing: "0.5px",
          }}
        >
          {worstTopPlans.length} Strategic Bottlenecks
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-3 gap-4">
        {worstTopPlans.map((plan, index) => (
          <Link
            key={plan.id}
            to={`/top-plans/${plan.id}`}
            style={{ textDecoration: "none" }}
          >
            <div
              className="rounded-lg h-full flex flex-col justify-between hover:shadow-md transition-shadow"
              style={{
                backgroundColor: "rgba(255, 227, 219, 0.48)",
                padding: "20px",
                border: `1px solid rgba(241, 90, 36, 0.15)`,
              }}
            >
              {/* Top section: Plan group tag + plan ID */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  {plan?.planGroup && (
                    <span
                      style={{
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        color: Theme().primary,
                        padding: "3px 8px",
                        borderRadius: "4px",
                        border: `1px solid ${Theme().primary}`,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {plan.planGroup}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#999",
                      fontFamily: "Inter, monospace",
                    }}
                  >
                    S0-{plan.id?.slice(-3) || index + 1}
                  </span>
                </div>

                {/* Title */}
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: Theme().textPrimary,
                    fontFamily: "Inter, sans-serif",
                    marginBottom: "6px",
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {plan?.title}
                </div>

                {/* Reason text */}
                <div
                  style={{
                    fontSize: "12px",
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    lineHeight: 1.4,
                  }}
                >
                  {getReasonText(plan, index)}
                </div>
              </div>

              {/* Bottom section: Avatar + Review link */}
              <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: "1px solid rgba(241, 90, 36, 0.1)" }}>
                <div className="flex items-center gap-2">
                  {plan?.assignedTo?.imageUrl ? (
                    <Avatar size={22} src={plan.assignedTo.imageUrl} />
                  ) : (
                    <Avatar size={22} style={{ backgroundColor: Theme().primary, fontSize: "10px" }}>
                      {plan?.assignedTo?.firstName?.[0] || <UserOutlined />}
                    </Avatar>
                  )}
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      fontFamily: "Inter, sans-serif",
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {plan?.assignedTo?.firstName || ""} {plan?.assignedTo?.lastName?.[0] ? plan.assignedTo.lastName[0] + "..." : ""}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: Theme().primary,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Review →
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default AttentionRequired;
